import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { SEO } from "@/components/SEO";
import { useCreateBlogPost, useUpdateBlogPost, useBlogPost } from "@/hooks/useBlogPosts";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { uploadBlogCoverImage } from "@/hooks/useBlogAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Eye, Edit, Upload, Loader2, ShieldAlert, CalendarIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export default function BlogEditor() {
  const { slug } = useParams<{ slug: string }>();
  const isEditMode = !!slug;
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const navigate = useNavigate();
  const createPost = useCreateBlogPost();
  const updatePost = useUpdateBlogPost();
  const { data: existingPost, isLoading: postLoading } = useBlogPost(slug || "");
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [title, setTitle] = useState("");
  const [postSlug, setPostSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [published, setPublished] = useState(false);
  const [publishDate, setPublishDate] = useState<Date | undefined>(undefined);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isEditMode && existingPost) {
      setTitle(existingPost.title);
      setPostSlug(existingPost.slug);
      setExcerpt(existingPost.excerpt);
      setContent(existingPost.content);
      setTags(existingPost.tags);
      setPublished(existingPost.published);
      setCoverImageUrl(existingPost.cover_image_url);
      setPublishDate(existingPost.published_at ? new Date(existingPost.published_at) : undefined);
    }
  }, [isEditMode, existingPost]);

  const generateSlug = (t: string) =>
    t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!isEditMode && (!postSlug || postSlug === generateSlug(title))) {
      setPostSlug(generateSlug(value));
    }
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag) && tags.length < 10) {
      setTags([...tags, tag]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => setTags(tags.filter((t) => t !== tag));

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }

    setUploading(true);
    try {
      const url = await uploadBlogCoverImage(file);
      setCoverImageUrl(url);
      toast.success("Cover image uploaded");
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to upload image";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!title || !postSlug || !excerpt || !content) {
      toast.error("Please fill in all required fields");
      return;
    }

    const publishedAt = published
      ? (publishDate ? publishDate.toISOString() : new Date().toISOString())
      : null;

    try {
      if (isEditMode && existingPost) {
        await updatePost.mutateAsync({
          id: existingPost.id,
          title,
          slug: postSlug,
          excerpt,
          content,
          tags,
          published,
          cover_image_url: coverImageUrl,
          published_at: publishedAt,
        });
        toast.success("Post updated!");
      } else {
        await createPost.mutateAsync({
          title,
          slug: postSlug,
          excerpt,
          content,
          tags,
          published,
          cover_image_url: coverImageUrl,
          published_at: publishedAt,
        });
        toast.success(published ? "Post published!" : "Draft saved!");
      }
      navigate("/blog");
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to save post";
      toast.error(msg);
    }
  };

  if (adminLoading || (isEditMode && postLoading)) {
    return (
      <PublicLayout>
        <div className="py-20 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PublicLayout>
    );
  }

  if (!isAdmin) {
    return (
      <PublicLayout>
        <div className="py-20 container px-4 text-center">
          <ShieldAlert className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h1 className="text-2xl font-bold mb-2">Admin Access Required</h1>
          <p className="text-muted-foreground mb-6">Only administrators can create or edit blog posts.</p>
          <Button asChild variant="outline">
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const isPending = createPost.isPending || updatePost.isPending;

  return (
    <PublicLayout>
      <SEO title={isEditMode ? "Edit Blog Post" : "New Blog Post"} noindex />
      <section className="py-20 md:py-28">
        <div className="container px-4 md:px-6 max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            <Button variant="ghost" size="sm" onClick={() => setPreview(!preview)}>
              {preview ? <Edit className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              {preview ? "Edit" : "Preview"}
            </Button>
          </div>

          <h1 className="text-3xl font-display font-bold mb-8">
            {isEditMode ? "Edit Blog Post" : "New Blog Post"}
          </h1>

          {preview ? (
            <div className="glass rounded-2xl p-8">
              {coverImageUrl && (
                <div className="w-full rounded-xl overflow-hidden mb-6 bg-background dark:bg-muted">
                  <img src={coverImageUrl} alt={title} className="w-full max-h-96 object-contain mx-auto" />
                </div>
              )}
              <h2 className="text-2xl font-bold mb-2">{title || "Untitled"}</h2>
              <p className="text-muted-foreground mb-4">{excerpt}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {tags.map((t) => (
                  <Badge key={t} variant="secondary">{t}</Badge>
                ))}
              </div>
              <div className="whitespace-pre-wrap text-muted-foreground">{content}</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cover image upload */}
              <div className="space-y-2">
                <Label>Cover Image</Label>
                <div className="flex items-start gap-4">
                  {coverImageUrl ? (
                    <div className="relative w-48 h-32 rounded-xl overflow-hidden border border-border bg-background dark:bg-muted">
                      <img src={coverImageUrl} alt="Cover" className="w-full h-full object-contain" />
                      <button
                        onClick={() => setCoverImageUrl(null)}
                        className="absolute top-1 right-1 p-1 rounded-full bg-background/80 hover:bg-background"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-48 h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors">
                      {uploading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground">Upload cover</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleCoverUpload}
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Full-quality upload, no compression</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input id="title" value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Your blog post title" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">URL Slug *</Label>
                <Input id="slug" value={postSlug} onChange={(e) => setPostSlug(e.target.value)} placeholder="your-blog-post-slug" />
                <p className="text-xs text-muted-foreground">/blog/{postSlug || "..."}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="excerpt">Excerpt *</Label>
                <Textarea id="excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Brief summary for blog listing and SEO" rows={2} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Content (Markdown) *</Label>
                <Textarea id="content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Write your post in Markdown..." rows={20} className="font-mono text-sm" />
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add a tag" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} />
                  <Button variant="outline" size="icon" onClick={addTag}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((t) => (
                    <Badge key={t} variant="secondary" className="gap-1">
                      {t}
                      <button onClick={() => removeTag(t)}><X className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-4 pt-4 border-t border-border">
                <div className="flex items-center gap-3">
                  <Switch checked={published} onCheckedChange={setPublished} />
                  <Label>Publish</Label>
                </div>
                {published && (
                  <div className="space-y-2">
                    <Label>Publish Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn("w-[240px] justify-start text-left font-normal", !publishDate && "text-muted-foreground")}
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {publishDate ? format(publishDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={publishDate}
                          onSelect={setPublishDate}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">Leave empty for now, or pick a past/future date.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <Button onClick={handleSubmit} disabled={isPending} className="gradient-primary text-primary-foreground">
              {isPending ? "Saving..." : isEditMode ? "Update Post" : published ? "Publish Post" : "Save Draft"}
            </Button>
            <Button variant="outline" asChild>
              <Link to="/blog">Cancel</Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
